/**
 * Formulation 流式会话逻辑
 */
import { nextTick, type Ref } from 'vue';
import type { AgentMessageResponse } from '@/types/formulation/formulation';
import { TASK_STATUS } from '../types';
import { hasExistingUserInput } from '../message-utils';
import { getStreamPayload } from './stream-payload';
import { createTaskPoller } from './task-poller';
import { createMessageUpdater } from './message-updater';
import { createHandlerManager } from './handlers';
import { createListAgentMessagePoller } from './list-message-poller';
import type { StreamConversationDeps, StreamContext, HandlerContext } from './types';

export type { StreamConversationDeps } from './types';

/**
 * 创建 streamConversation 函数
 */
export function createStreamConversation(deps: StreamConversationDeps) {
  const {
    sessionId,
    conversationData,
    currentIteration,
    selectedToolKey,
    pipelineRunning,
    taskStatus,
    shouldScrollToBottom,
    materialFormulationApiService,
    setSelectedTool,
    tryAutoOpenLastToolResult,
    clearPendingUserMessage,
    onSseConnectionError,
  } = deps;

  // 创建处理器管理器
  const handlerManager = createHandlerManager();

  /**
   * 流式会话处理
   */
  const streamConversation = async (
    sessionIdValue: string,
    contextIdValue: Ref<number> | undefined,
    content: string,
    options?: { skipAddUserMessage?: boolean },
  ) => {
    const request: { user_input: string; context_id?: number } = { user_input: content };
    const effectiveContextId = contextIdValue?.value;
    if (effectiveContextId) {
      request.context_id = effectiveContextId;
    }

    const stream = materialFormulationApiService.formulate(sessionIdValue, request);
    const existing = conversationData.value ?? [];
    const shouldAddUserMessage =
      !options?.skipAddUserMessage && !hasExistingUserInput(existing, content);
    const roundStartIso = new Date().toISOString();
    const streamIteration = Math.max(1, (currentIteration.value || 0) + 1);

    // 添加用户消息
    if (shouldAddUserMessage) {
      const userMessage: AgentMessageResponse = {
        id: Date.now(),
        business_type: 'user_input',
        session_id: sessionIdValue,
        iteration: streamIteration,
        tool_key: 'user_input',
        content: { raw: content },
        tool_state: TASK_STATUS.COMPLETED,
        gmt_created: roundStartIso,
      };
      conversationData.value = [...existing, userMessage];
      clearPendingUserMessage();
      await nextTick();
      shouldScrollToBottom.value = true;
    }

    stream.start();
    taskStatus.value = TASK_STATUS.RUNNING;

    // 创建轮询器
    const poller = createTaskPoller({
      sessionId,
      taskStatus,
      pipelineRunning,
      api: materialFormulationApiService,
    });
    poller.start(sessionIdValue);

    // 创建流上下文
    const streamContext: StreamContext = {
      sessionIdValue,
      streamIteration,
      roundStartIso,
      deps,
    };

    // 创建消息更新器
    const messageUpdater = createMessageUpdater(
      { conversationData, selectedToolKey, setSelectedTool },
      streamContext,
    );

    return new Promise<void>((resolve) => {
      /** 消息列表轮询的 stop 句柄（SSE 报错后启动轮询时设置） */
      let listMessagePollerStop: (() => void) | null = null;

      /** 结束流式处理 */
      const finishStreamAndFetchFinal = () => {
        listMessagePollerStop?.();
        listMessagePollerStop = null;
        poller.stop();
        pipelineRunning.value = false;

        if (sessionId.value === sessionIdValue) {
          // 使用流式过程中已积累的 conversationData 打开最后一个工具结果
          tryAutoOpenLastToolResult(conversationData.value);
        }
        resolve();
      };

      // 创建处理器上下文
      const handlerContext: HandlerContext = {
        messageUpdater,
        streamContext,
        finishStream: finishStreamAndFetchFinal,
      };

      stream
        .onMessage((m) => {
          try {
            const data = getStreamPayload(m);
            if (!data) return;

            handlerManager.handleMessage(data, handlerContext);
          } catch (error) {
            console.error('[Formulation] Failed to parse stream message:', error, m);
          }
        })
        .onFinish(() => {
          // SSE 连接关闭时结束流
          console.debug('[Formulation] SSE stream finished');
          finishStreamAndFetchFinal();
        })
        .onError((error) => {
          console.error('[Formulation] SSE stream error:', error);
          poller.stop();
          pipelineRunning.value = false;
          if (onSseConnectionError?.(error)) {
            finishStreamAndFetchFinal();
            return;
          }
          // SSE 异常时改为轮询 listAgentMessages 同步消息，直到任务结束再 finish
          const listMessagePoller = createListAgentMessagePoller(
            sessionIdValue,
            {
              sessionId,
              taskStatus,
              conversationData,
              materialFormulationApiService,
              tryAutoOpenLastToolResult,
            },
            finishStreamAndFetchFinal,
          );
          listMessagePollerStop = listMessagePoller.stop;
        });
    });
  };

  return streamConversation;
}
